// ファイル名: assets/section-firstview-carousel.js
// 改修版：パフォーマンス最適化と機能拡張

class FirstviewCarousel {
  constructor(element) {
    this.carousel = element;
    this.sectionId = element.dataset.sectionId;
    this.autoplay = element.dataset.autoplay === 'true';
    this.autoplaySpeed = parseInt(element.dataset.autoplaySpeed) || 5000;
    
    // DOM要素の取得
    this.track = element.querySelector('.firstview-carousel__track');
    this.slides = element.querySelectorAll('.firstview-carousel__slide');
    this.prevButton = element.querySelector('.firstview-carousel__nav--prev');
    this.nextButton = element.querySelector('.firstview-carousel__nav--next');
    this.indicators = element.querySelectorAll('.firstview-carousel__indicator');
    
    // 状態管理
    this.currentSlide = 0;
    this.totalSlides = this.slides.length;
    this.autoplayTimer = null;
    this.isTransitioning = false;
    this.isVisible = false;
    this.isTouching = false;
    
    // バインドメソッド
    this.handlePrevClick = this.prevSlide.bind(this);
    this.handleNextClick = this.nextSlide.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleMouseEnter = this.stopAutoplay.bind(this);
    this.handleMouseLeave = this.conditionalStartAutoplay.bind(this);
    this.handleResize = this.debounce(this.updateSlidePosition.bind(this), 250);
    
    // 初期化
    this.init();
  }

  init() {
    if (this.totalSlides <= 1) {
      this.hideNavigation();
      return;
    }
    
    this.setupEventListeners();
    this.setupKeyboardNavigation();
    this.setupTouchNavigation();
    this.setupIntersectionObserver();
    this.setupA11y();
    
    // 初期状態の設定
    this.updateSlidePosition();
    this.updateIndicators();
    this.updateAriaLabels();
    
    // 要素に参照を保存（クリーンアップ用）
    this.carousel.carouselInstance = this;
    
    console.log(`Firstview Carousel initialized: ${this.sectionId}`);
  }

  setupEventListeners() {
    // ナビゲーションボタン
    if (this.prevButton) {
      this.prevButton.addEventListener('click', this.handlePrevClick);
    }
    
    if (this.nextButton) {
      this.nextButton.addEventListener('click', this.handleNextClick);
    }
    
    // インジケーター
    this.indicators.forEach((indicator, index) => {
      indicator.addEventListener('click', () => this.goToSlide(index));
    });
    
    // マウスホバーでオートプレイ制御
    this.carousel.addEventListener('mouseenter', this.handleMouseEnter);
    this.carousel.addEventListener('mouseleave', this.handleMouseLeave);
    
    // ウィンドウリサイズ対応
    window.addEventListener('resize', this.handleResize);
    
    // フォーカス管理
    this.carousel.addEventListener('focusin', this.stopAutoplay.bind(this));
    this.carousel.addEventListener('focusout', this.conditionalStartAutoplay.bind(this));
  }

  setupKeyboardNavigation() {
    // カルーセル全体にtabindexを設定
    this.carousel.setAttribute('tabindex', '0');
    
    this.carousel.addEventListener('keydown', this.handleKeydown);
  }

  handleKeydown(e) {
    // フォーカスがカルーセル内にある場合のみ処理
    if (!this.carousel.contains(document.activeElement)) return;
    
    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        this.prevSlide();
        break;
      case 'ArrowRight':
        e.preventDefault();
        this.nextSlide();
        break;
      case 'Home':
        e.preventDefault();
        this.goToSlide(0);
        break;
      case 'End':
        e.preventDefault();
        this.goToSlide(this.totalSlides - 1);
        break;
      case 'Space':
      case 'Enter':
        if (e.target.classList.contains('firstview-carousel__indicator')) {
          e.preventDefault();
          const index = parseInt(e.target.dataset.slideIndex);
          this.goToSlide(index);
        }
        break;
    }
  }

  setupTouchNavigation() {
    let startX = 0;
    let startY = 0;
    let currentX = 0;
    let currentY = 0;
    let distX = 0;
    let distY = 0;
    const threshold = 50;
    const restraint = 100;
    
    const handleTouchStart = (e) => {
      this.isTouching = true;
      const touch = e.touches[0];
      startX = touch.clientX;
      startY = touch.clientY;
      this.stopAutoplay();
    };
    
    const handleTouchMove = (e) => {
      if (!this.isTouching) return;
      
      const touch = e.touches[0];
      currentX = touch.clientX;
      currentY = touch.clientY;
      distX = currentX - startX;
      distY = currentY - startY;
      
      // 縦スクロールの場合は処理しない
      if (Math.abs(distY) > restraint) {
        this.isTouching = false;
        return;
      }
      
      // 横スワイプの場合はデフォルトの動作を防ぐ
      if (Math.abs(distX) > 10) {
        e.preventDefault();
      }
    };
    
    const handleTouchEnd = () => {
      if (!this.isTouching) return;
      
      this.isTouching = false;
      
      // スワイプ距離が閾値を超えていて、主に横方向の場合
      if (Math.abs(distX) > threshold && Math.abs(distY) < restraint) {
        if (distX > 0) {
          this.prevSlide();
        } else {
          this.nextSlide();
        }
      }
      
      // 変数をリセット
      startX = 0;
      startY = 0;
      currentX = 0;
      currentY = 0;
      distX = 0;
      distY = 0;
      
      this.conditionalStartAutoplay();
    };
    
    // パッシブイベントリスナーでパフォーマンス向上
    this.carousel.addEventListener('touchstart', handleTouchStart, { passive: true });
    this.carousel.addEventListener('touchmove', handleTouchMove, { passive: false });
    this.carousel.addEventListener('touchend', handleTouchEnd, { passive: true });
    this.carousel.addEventListener('touchcancel', handleTouchEnd, { passive: true });
  }

  setupIntersectionObserver() {
    const observerOptions = {
      threshold: 0.3,
      rootMargin: '0px 0px -50px 0px'
    };
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        this.isVisible = entry.isIntersecting;
        
        if (entry.isIntersecting) {
          this.carousel.classList.add('firstview-carousel--visible');
          this.conditionalStartAutoplay();
        } else {
          this.carousel.classList.remove('firstview-carousel--visible');
          this.stopAutoplay();
        }
      });
    }, observerOptions);
    
    observer.observe(this.carousel);
    this.intersectionObserver = observer;
  }

  setupA11y() {
    // カルーセル全体のrole属性
    this.carousel.setAttribute('aria-roledescription', 'carousel');
    
    // ライブリージョンの設定
    if (this.track) {
      this.track.setAttribute('aria-live', 'polite');
    }
    
    // ナビゲーションボタンのアクセシビリティ
    if (this.prevButton && this.nextButton) {
      this.prevButton.setAttribute('aria-controls', this.track.id || 'carousel-track');
      this.nextButton.setAttribute('aria-controls', this.track.id || 'carousel-track');
    }
  }

  nextSlide() {
    if (this.isTransitioning) return;
    
    const nextIndex = (this.currentSlide + 1) % this.totalSlides;
    this.goToSlide(nextIndex, 'next');
  }

  prevSlide() {
    if (this.isTransitioning) return;
    
    const prevIndex = (this.currentSlide - 1 + this.totalSlides) % this.totalSlides;
    this.goToSlide(prevIndex, 'prev');
  }

  goToSlide(index, direction = null) {
    if (this.isTransitioning || index === this.currentSlide || index < 0 || index >= this.totalSlides) {
      return;
    }
    
    this.isTransitioning = true;
    const previousSlide = this.currentSlide;
    this.currentSlide = index;
    
    // スライド変更のアナウンス
    this.announceSlideChange(direction);
    
    this.updateSlidePosition();
    this.updateIndicators();
    this.updateAriaLabels();
    this.updateNavButtonStates();
    
    // カスタムイベントの発火
    this.dispatchSlideChangeEvent(previousSlide, index);
    
    // トランジション終了後にフラグをリセット
    setTimeout(() => {
      this.isTransitioning = false;
    }, 600);
  }

  updateSlidePosition() {
    // アクティブなスライドの更新
    this.slides.forEach((slide, index) => {
      const isActive = index === this.currentSlide;
      slide.classList.toggle('firstview-carousel__slide--active', isActive);
      slide.setAttribute('aria-hidden', !isActive);
      
      // フォーカス可能要素の管理
      const focusableElements = slide.querySelectorAll('a, button, input, select, textarea, [tabindex]');
      focusableElements.forEach(el => {
        el.setAttribute('tabindex', isActive ? '0' : '-1');
      });
    });
    
    // トラックの位置更新（CSS transformではなくopacityで制御）
    // 既存のCSSでopacityベースのトランジションを使用
  }

  updateIndicators() {
    this.indicators.forEach((indicator, index) => {
      const isActive = index === this.currentSlide;
      indicator.classList.toggle('firstview-carousel__indicator--active', isActive);
      indicator.setAttribute('aria-selected', isActive);
      indicator.setAttribute('tabindex', isActive ? '0' : '-1');
    });
  }

  updateAriaLabels() {
    this.slides.forEach((slide, index) => {
      slide.setAttribute('aria-label', `スライド ${index + 1} / ${this.totalSlides}`);
    });
    
    // 現在のスライド情報をライブリージョンで通知
    if (this.track) {
      this.track.setAttribute('aria-label', `${this.totalSlides}枚中${this.currentSlide + 1}枚目のスライドを表示中`);
    }
  }

  updateNavButtonStates() {
    // 循環しない場合のボタン状態管理（必要に応じて）
    if (this.prevButton && this.nextButton) {
      this.prevButton.setAttribute('aria-disabled', 'false');
      this.nextButton.setAttribute('aria-disabled', 'false');
    }
  }

  announceSlideChange(direction) {
    // スクリーンリーダー用のアナウンス
    const announcement = direction 
      ? `${direction === 'next' ? '次' : '前'}のスライドに移動しました。${this.currentSlide + 1}枚目です。`
      : `${this.currentSlide + 1}枚目のスライドに移動しました。`;
    
    this.announceToScreenReader(announcement);
  }

  announceToScreenReader(message) {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.style.cssText = 'position:absolute;left:-10000px;width:1px;height:1px;overflow:hidden;';
    announcement.textContent = message;
    
    document.body.appendChild(announcement);
    
    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  }

  dispatchSlideChangeEvent(previousIndex, currentIndex) {
    const event = new CustomEvent('carousel:slideChange', {
      detail: {
        previousIndex,
        currentIndex,
        totalSlides: this.totalSlides,
        carousel: this.carousel
      }
    });
    
    this.carousel.dispatchEvent(event);
  }

  conditionalStartAutoplay() {
    if (this.autoplay && this.isVisible && !this.isTouching) {
      this.startAutoplay();
    }
  }

  startAutoplay() {
    if (!this.autoplay || this.totalSlides <= 1) return;
    
    this.stopAutoplay();
    this.autoplayTimer = setInterval(() => {
      if (!this.isTouching && this.isVisible) {
        this.nextSlide();
      }
    }, this.autoplaySpeed);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  hideNavigation() {
    if (this.prevButton) this.prevButton.style.display = 'none';
    if (this.nextButton) this.nextButton.style.display = 'none';
    this.indicators.forEach(indicator => indicator.style.display = 'none');
  }

  // ユーティリティ関数
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // パブリックAPI
  play() {
    this.autoplay = true;
    this.startAutoplay();
  }

  pause() {
    this.stopAutoplay();
  }

  goto(index) {
    this.goToSlide(index);
  }

  // パフォーマンス最適化のためのデストラクタ
  destroy() {
    this.stopAutoplay();
    
    // イベントリスナーの削除
    if (this.prevButton) {
      this.prevButton.removeEventListener('click', this.handlePrevClick);
    }
    if (this.nextButton) {
      this.nextButton.removeEventListener('click', this.handleNextClick);
    }
    
    this.carousel.removeEventListener('mouseenter', this.handleMouseEnter);
    this.carousel.removeEventListener('mouseleave', this.handleMouseLeave);
    this.carousel.removeEventListener('keydown', this.handleKeydown);
    window.removeEventListener('resize', this.handleResize);
    
    // Intersection Observerの削除
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    
    // 参照の削除
    if (this.carousel.carouselInstance) {
      delete this.carousel.carouselInstance;
    }
    
    console.log(`Firstview Carousel destroyed: ${this.sectionId}`);
  }
}

// DOM読み込み完了後に初期化
document.addEventListener('DOMContentLoaded', () => {
  const carousels = document.querySelectorAll('.firstview-carousel');
  
  carousels.forEach((carousel) => {
    try {
      new FirstviewCarousel(carousel);
    } catch (error) {
      console.error('Failed to initialize carousel:', error);
    }
  });
});

// Shopifyセクション設定変更時の再初期化
document.addEventListener('shopify:section:load', (event) => {
  const carousel = event.target.querySelector('.firstview-carousel');
  if (carousel) {
    try {
      new FirstviewCarousel(carousel);
    } catch (error) {
      console.error('Failed to reinitialize carousel:', error);
    }
  }
});

// セクション削除時のクリーンアップ
document.addEventListener('shopify:section:unload', (event) => {
  const carousel = event.target.querySelector('.firstview-carousel');
  if (carousel && carousel.carouselInstance) {
    carousel.carouselInstance.destroy();
  }
});

// Shopifyセクション設定変更時の更新
document.addEventListener('shopify:section:select', (event) => {
  const carousel = event.target.querySelector('.firstview-carousel');
  if (carousel && carousel.carouselInstance) {
    carousel.carouselInstance.pause();
  }
});

document.addEventListener('shopify:section:deselect', (event) => {
  const carousel = event.target.querySelector('.firstview-carousel');
  if (carousel && carousel.carouselInstance) {
    carousel.carouselInstance.conditionalStartAutoplay();
  }
});

// グローバルエクスポート（必要に応じて）
window.FirstviewCarousel = FirstviewCarousel;